<script setup lang="ts">
import {
	dataTableContext,
	DataTableMetric,
	isPickMetric,
} from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import { computed, inject } from "vue";
import { formatNumber } from "chartjs-graph";
import CompareTooltip from "@/modules/explorer/components/Metrics/Table/CompareTooltip.vue";
import DataTableResponseMetricDTO = App.Data.Explorer.DataTable.DataTableResponseMetricDTO;
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import { useExplorerStore } from "@/modules/explorer/store";

const { metric, dataTable } = defineProps<{
	dataTable: DataTableResponseDTO;
	metric: DataTableResponseMetricDTO;
}>();

const { openContextMenu, dataTableIndex } = inject(dataTableContext);
const metricObject = computed(() =>
	DataTableMetric.fromResponseMetric(metric, dataTable)
);

const singleMetric = computed(() => useExplorerStore().document.single_metric);
const singleMetricPick = computed(() =>
	isPickMetric(singleMetric.value) &&
	dataTableIndex === singleMetric.value.table_index
		? singleMetric.value
		: null
);

const isSelectedSingleMetric = computed(
	() =>
		singleMetricPick.value?.metric_name === metricObject.value.name &&
		singleMetricPick.value?.horizontal_dimension_value ===
			metricObject.value.row.horizontal &&
		singleMetricPick.value?.offset_dimension_value ===
			metricObject.value.subRow.offset
);

const currentYearValueColumn = computed(() =>
	dataTable.dataTableDO.split_prediction ? "actualValue" : "totalValue"
);
</script>

<template>
	<td
		class="metrics-data-table__metric"
		:class="[
			`metrics-data-table--color-1`,
			{
				'metrics-data-table__metric--single-metric':
					isSelectedSingleMetric &&
					singleMetricPick.value_column === currentYearValueColumn &&
					singleMetricPick.comparison_value === 'Y',
			},
		]"
		@contextmenu.prevent="
			(e) => openContextMenu(e, metricObject, 0, currentYearValueColumn, false)
		"
	>
		<div class="align-center">
			{{
				formatNumber(metric.values[0][currentYearValueColumn], metric.format)
			}}
			<compare-tooltip
				v-if="
					dataTable.dataTableDO.is_comparison_nested &&
					metricObject?.hasComparison
				"
				:metric="metricObject"
				:show-change-relative-to-current="
					dataTable.dataTableDO.show_change_relative_to_current
				"
				:show-change-absolute="dataTable.dataTableDO.show_change_absolute"
			/>
		</div>
	</td>
	<td
		v-if="dataTable.dataTableDO.split_prediction"
		class="metrics-data-table__metric metrics-data-table--color-1"
		:class="{
			'metrics-data-table__metric--single-metric':
				isSelectedSingleMetric &&
				singleMetricPick.value_column === 'totalValue' &&
				singleMetricPick.comparison_value === 'Y',
		}"
		@contextmenu.prevent="
			(e) => openContextMenu(e, metricObject, 0, 'totalValue', false)
		"
	>
		{{ formatNumber(metric.values[0].totalValue, metric.format) }}
	</td>
	<template v-if="!dataTable.dataTableDO.is_comparison_nested">
		<template v-for="(value, idx) in metric.values.slice(1)" :key="idx">
			<td
				class="metrics-data-table__metric"
				:class="[
					`metrics-data-table--color-${idx + 2}`,
					{
						'metrics-data-table__metric--single-metric':
							isSelectedSingleMetric &&
							singleMetricPick.value_column === 'totalValue' &&
							singleMetricPick.comparison_value === value.comparison,
					},
				]"
				@contextmenu.prevent="
					(e) => openContextMenu(e, metricObject, idx + 1, 'totalValue', false)
				"
			>
				{{ formatNumber(value.totalValue, metric.format) }}
			</td>
			<td
				v-if="dataTable.dataTableDO.show_change_absolute"
				class="metrics-data-table__metric"
				:class="[
					`metrics-data-table--color-${idx + 2}`,
					{
						'metrics-data-table__metric--single-metric':
							isSelectedSingleMetric &&
							singleMetricPick.value_column === 'deltaAbsolute' &&
							singleMetricPick.comparison_value === value.comparison,
					},
				]"
				@contextmenu.prevent="
					(e) =>
						openContextMenu(e, metricObject, idx + 1, 'deltaAbsolute', false)
				"
			>
				{{ formatNumber(value.deltaAbsolute, metric.format) }}
			</td>
			<td
				v-if="dataTable.dataTableDO.show_change_relative_to_current"
				class="metrics-data-table__metric"
				:class="[
					`metrics-data-table--color-${idx + 2}`,
					{
						'metrics-data-table__metric--single-metric':
							isSelectedSingleMetric &&
							singleMetricPick.value_column === 'deltaRelative' &&
							singleMetricPick.comparison_value === value.comparison,
					},
				]"
				@contextmenu.prevent="
					(e) =>
						openContextMenu(e, metricObject, idx + 1, 'deltaRelative', false)
				"
			>
				{{ formatNumber(value.deltaRelative, "percentage") }}
			</td>
		</template>
	</template>
</template>

<style scoped lang="scss"></style>
